import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const money = (v) => Number(v || 0).toFixed(2);

export default function CompanyReportsDashboardPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();
  const [loading, setLoading] = useState(false);
  const [trial, setTrial] = useState([]);
  const [pl, setPl] = useState([]);
  const [bs, setBs] = useState([]);

  const companyId = user?.companyId;
  const query = useMemo(
    () =>
      `fromDate=${financialYear.fromDate}&toDate=${financialYear.toDate}${companyId ? `&companyId=${companyId}` : ""}`,
    [financialYear, companyId],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [tbRes, plRes, bsRes] = await Promise.all([
          client.get(`/finance/trial-balance?${query}`),
          client.get(`/finance/profit-loss?${query}`),
          client.get(`/finance/balance-sheet?${query}`),
        ]);
        setTrial(Array.isArray(tbRes.data) ? tbRes.data : []);
        setPl(Array.isArray(plRes.data) ? plRes.data : []);
        setBs(Array.isArray(bsRes.data) ? bsRes.data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [query]);

  const totalDebit = trial.reduce(
    (s, r) => s + Number(r.TotalDebit || r.totalDebit || 0),
    0,
  );
  const totalCredit = trial.reduce(
    (s, r) => s + Number(r.TotalCredit || r.totalCredit || 0),
    0,
  );
  const income = pl
    .filter((x) => (x.AccountType ?? x.accountType) === "Income")
    .reduce((s, r) => s + Number(r.Amount || r.amount || 0), 0);
  const expense = pl
    .filter((x) => (x.AccountType ?? x.accountType) === "Expense")
    .reduce((s, r) => s + Number(r.Amount || r.amount || 0), 0);
  const assets = bs
    .filter((x) => (x.AccountType ?? x.accountType) === "Asset")
    .reduce((s, r) => s + Number(r.Amount || r.amount || 0), 0);
  const liabilitiesEquity = bs
    .filter((x) => (x.AccountType ?? x.accountType) !== "Asset")
    .reduce((s, r) => s + Number(r.Amount || r.amount || 0), 0);

  const card = {
    border: 0,
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 12, padding: 0 }}>
      <div className="card mb-3" style={card}>
        <div className="card-body d-flex justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="fw-bold mb-1">Company Wise Reports</h5>
            <div className="text-muted small">
              <strong>{user?.companyName || "Setup required"}</strong> •{" "}
              {financialYear.fromDate} to {financialYear.toDate}
            </div>
            <div className="text-muted small">
              GSTIN: <strong>{user?.companyGstin || "-"}</strong> • Phone:{" "}
              <strong>{user?.companyPhone || "-"}</strong>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {loading && <span className="badge text-bg-info">Loading...</span>}
            <Link className="btn btn-primary btn-sm" to="/trial-balance">
              Trial Balance
            </Link>
            <Link className="btn btn-outline-primary btn-sm" to="/profit-loss">
              P&L
            </Link>
            <Link
              className="btn btn-outline-primary btn-sm"
              to="/balance-sheet"
            >
              Balance Sheet
            </Link>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <Summary
          title="Trial Debit"
          value={money(totalDebit)}
          icon="bi-calculator"
        />
        <Summary
          title="Trial Credit"
          value={money(totalCredit)}
          icon="bi-calculator-fill"
        />
        <Summary
          title="Net Profit / Loss"
          value={money(income - expense)}
          icon="bi-graph-up-arrow"
        />
        <Summary title="Assets" value={money(assets)} icon="bi-building" />
        <Summary
          title="Liabilities + Equity"
          value={money(liabilitiesEquity)}
          icon="bi-bank"
        />
      </div>
    </div>
  );
}

function Summary({ title, value, icon }) {
  return (
    <div className="col-md-4 col-xl-2">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ borderRadius: 14 }}
      >
        <div className="card-body">
          <i className={`bi ${icon} text-primary`}></i>
          <div className="text-muted small mt-2">{title}</div>
          <div className="fw-bold fs-6">{value}</div>
        </div>
      </div>
    </div>
  );
}
